import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import { queryKeys } from "./keys";
import type {
  BulkDismissPendingActionsInput,
  CreatePendingActionInput,
  ListResponse,
  PendingAction,
  ResolvePendingActionInput,
  UpdatePendingActionInput,
} from "./types";

const base = "/v1/pending-actions";

export function usePendingActionsForGrow(
  growID: string | undefined,
  includeDismissed = false,
) {
  return useQuery({
    queryKey: growID
      ? queryKeys.pendingActions.forGrow(growID, includeDismissed)
      : queryKeys.pendingActions.forGrow("", false),
    queryFn: ({ signal }) =>
      api.get<ListResponse<PendingAction>>(
        base,
        {
          grow_id: growID,
          include_dismissed: includeDismissed ? "1" : undefined,
        },
        signal,
      ),
    enabled: !!growID,
  });
}

// Open pending actions across several grows at once, for the dashboard
// "Next steps" roll-up. Uses the same per-grow query keys as
// usePendingActionsForGrow, so it shares cache with the grow cards rather than
// re-fetching. Returns the flattened open actions plus an aggregate loading
// flag; callers sort/slice as they like.
export function usePendingActionsAcrossGrows(growIDs: string[]) {
  const results = useQueries({
    queries: growIDs.map((id) => ({
      queryKey: queryKeys.pendingActions.forGrow(id, false),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.get<ListResponse<PendingAction>>(base, { grow_id: id }, signal),
    })),
  });
  const items = results.flatMap((r) => r.data?.items ?? []);
  const isLoading = results.some((r) => r.isLoading);
  return { items, isLoading };
}

export function useCreatePendingAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePendingActionInput) =>
      api.post<PendingAction>(base, input),
    onSuccess: (created) => {
      qc.invalidateQueries({
        queryKey: queryKeys.pendingActions.forGrowAll(created.grow_id),
      });
    },
  });
}

export function useUpdatePendingAction(id: string, growID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePendingActionInput) =>
      api.patch<PendingAction>(`${base}/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.pendingActions.forGrowAll(growID),
      });
    },
  });
}

export function useDismissPendingAction(id: string, growID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string | undefined) =>
      api.delete<void>(`${base}/${id}`, { reason: reason ?? "" }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.pendingActions.forGrowAll(growID),
      });
    },
  });
}

// Resolve records a real outcome for a pending action ("completed" /
// "skipped" / "dismissed"). The server writes an audit row against the owning
// grow, so the resolution shows up in the grow's activity log — unlike the
// soft DELETE-dismiss, which only flips dismissed_at. The colon path mirrors
// `${base}:bulk-dismiss`. onSuccess invalidates the per-grow cache so the row
// drops off the open list.
export function useResolvePendingAction(id: string, growID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ResolvePendingActionInput) =>
      api.post<void>(`${base}/${id}:resolve`, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.pendingActions.forGrowAll(growID),
      });
    },
  });
}

// Like useDismissPendingAction but the id is supplied per call, so a single
// hook instance can dismiss any of several actions (e.g. undoing a just-added
// AI suggestion, where the id isn't known until the create resolves).
export function useDismissPendingActionByID(growID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.delete<void>(`${base}/${id}`, { reason: reason ?? "" }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.pendingActions.forGrowAll(growID),
      });
    },
  });
}

export function useBulkDismissPendingActions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkDismissPendingActionsInput) =>
      api.post<void>(`${base}:bulk-dismiss`, input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: queryKeys.pendingActions.forGrowAll(vars.grow_id),
      });
    },
  });
}
